import { useCallback, useEffect, useRef, useState } from 'react';
import { uploadFile, listPastDueImports } from '../../services/kollectaApi';
import styles from './style.module.css';
import Modal from '../Modal';
import errorIcon from '../../assets/images/error.svg';
import uploadIcon from './assets/upload-icon.svg';
import excelFileIcon from './assets/excel-file.svg';
import statusProcessingIcon from './assets/status-processing.svg';
import statusFailedIcon from './assets/status-failed.svg';
import statusSuccessIcon from './assets/status-success.svg';
import closeIcon from './assets/close.svg';

const POLL_INTERVAL_MS = 3000;
const MAX_FILE_BYTES = 20 * 1024 * 1024;
const ACTIVE_STATUSES = ['queued', 'processing'];

const STATUS_META = {
  queued: {
    label: 'Em processamento',
    badgeClass: 'badgeProcessing',
    icon: statusProcessingIcon,
    showProgress: true,
  },
  processing: {
    label: 'Em processamento',
    badgeClass: 'badgeProcessing',
    icon: statusProcessingIcon,
    showProgress: true,
  },
  failed: {
    label: 'Falha na importação',
    badgeClass: 'badgeFailed',
    icon: statusFailedIcon,
    showProgress: false,
  },
  completed: {
    label: 'Concluído',
    badgeClass: 'badgeSuccess',
    icon: statusSuccessIcon,
    showProgress: false,
  },
};

const formatFileSize = (bytes = 0) => {
  const value = Number(bytes) || 0;
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
};

const formatRelativeTime = (dateValue) => {
  if (!dateValue) return '';
  const diffMs = Date.now() - new Date(dateValue).getTime();
  if (Number.isNaN(diffMs) || diffMs < 0) return 'agora';
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return 'agora';
  if (minutes < 60) return `há ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `há ${hours} h`;
  const days = Math.floor(hours / 24);
  return `há ${days} d`;
};

const extractUploadError = (error) => {
  const data = error?.data || error;
  const missingHeaders = data?.missingHeaders;
  const message = data?.error || data?.message || error?.message || 'Não foi possível importar o arquivo.';
  if (Array.isArray(missingHeaders) && missingHeaders.length) {
    return `${message} ${missingHeaders.join(', ')}`;
  }
  return message;
};

const UploadFileAndList = ({ showList, dataForm, setDataFormAfterUse, exampleFile }) => {
  const filesElement = useRef(null);
  const [imports, setImports] = useState([]);
  const [dismissedIds, setDismissedIds] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [enqueueNotice, setEnqueueNotice] = useState('');
  const [popUpFeedBack, setPopUpFeedBack] = useState({
    loading: false,
    typeError: false,
    message: '',
  });

  const fetchImports = useCallback(async () => {
    if (!showList) return;
    try {
      const response = await listPastDueImports({ limit: 50, offset: 0 });
      setImports(response?.data?.rows || []);
    } catch (error) {
      console.error(error);
    }
  }, [showList]);

  useEffect(() => {
    fetchImports();
  }, [fetchImports]);

  const hasActiveImports = imports.some((item) => ACTIVE_STATUSES.includes(item.status));

  useEffect(() => {
    if (!showList || !hasActiveImports) return undefined;
    const timer = setInterval(fetchImports, POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [showList, hasActiveImports, fetchImports]);

  const sendDebtFiles = async (fileList) => {
    const selected = Array.from(fileList || []).filter(Boolean);
    if (!selected.length) return;

    const invalidType = selected.find((file) => !/\.(csv|xlsx|xls)$/i.test(file.name));
    if (invalidType) {
      setPopUpFeedBack({
        loading: false,
        typeError: true,
        message: 'Envie um arquivo Excel (.xlsx, .xls) ou CSV.',
      });
      return;
    }

    const oversized = selected.find((file) => file.size > MAX_FILE_BYTES);
    if (oversized) {
      setPopUpFeedBack({
        loading: false,
        typeError: true,
        message: 'Cada arquivo pode ter no máximo 20 MB.',
      });
      return;
    }

    const formData = new FormData();
    selected.forEach((file) => formData.append('file', file));

    setPopUpFeedBack({ loading: true, typeError: false, message: '' });
    setEnqueueNotice('');

    try {
      await uploadFile(formData);
      setPopUpFeedBack({ loading: false, typeError: false, message: '' });
      setEnqueueNotice('Importação em andamento. Você receberá um e-mail com o resultado.');
      if (filesElement.current) {
        filesElement.current.value = '';
      }
      await fetchImports();
    } catch (error) {
      setPopUpFeedBack({
        loading: false,
        typeError: true,
        message: extractUploadError(error),
      });
    }
  };

  const handlePostWallet = async () => {
    try {
      const selected = Array.from(filesElement.current?.files || []);
      selected.forEach((file) => {
        dataForm.append('file', file);
        setDataFormAfterUse(dataForm);
      });
    } catch (error) {
      alert('Não foi possível importar o arquivo!');
    }
  };

  const handleFileChange = (event) => {
    if (showList) {
      sendDebtFiles(event.target.files);
      return;
    }
    handlePostWallet();
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);
    if (showList) {
      sendDebtFiles(event.dataTransfer.files);
      return;
    }
    if (filesElement.current) {
      filesElement.current.files = event.dataTransfer.files;
    }
    handlePostWallet();
  };

  const visibleImports = imports.filter((item) => !dismissedIds.includes(item.id));

  return (
    <div className={styles.uploadFileContainer}>
      <input
        className={styles.inputFile}
        id="past-due-import-file"
        type="file"
        accept=".csv,.xlsx,.xls"
        multiple={Boolean(showList)}
        onChange={handleFileChange}
        ref={filesElement}
      />

      <label
        htmlFor="past-due-import-file"
        className={`${styles.dropzone} ${isDragging ? styles.dropzoneActive : ''} ${popUpFeedBack.loading ? styles.dropzoneDisabled : ''}`}
        onDragOver={(event) => {
          event.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
      >
        <img
          className={styles.uploadIcon}
          src={uploadIcon}
          alt=""
          width={32}
          height={32}
        />
        <p className={styles.dropzoneTitle}>
          Arraste e solte os arquivos aqui
          {' '}
          <span>ou procure no computador</span>
        </p>
        <p className={styles.dropzoneHint}>Arquivos ilimitados, até 20 MB cada</p>
      </label>

      <p className={styles.formatHint}>
        Arquivo no formato Excel
        {exampleFile && (
          <>
            {' · '}
            <a href={exampleFile} target="_blank" rel="noreferrer">
              baixar modelo
            </a>
          </>
        )}
      </p>

      {enqueueNotice && <p className={styles.enqueueNotice}>{enqueueNotice}</p>}

      {showList && visibleImports.length > 0 && (
        <section className={styles.historyCard}>
          <div className={styles.historyTitles}>
            <h2>Arquivos importados</h2>
            <p>Acompanhe o status dos seus uploads</p>
          </div>

          <div className={styles.historyList}>
              {visibleImports.map((item) => {
                const meta = STATUS_META[item.status] || STATUS_META.processing;
                const progress = Number(item.progressPercent) || 0;
                return (
                  <article key={item.id} className={styles.historyRow}>
                    <div className={styles.historyRowMain}>
                      <div className={styles.fileInfo}>
                        <img
                          className={styles.excelIcon}
                          src={excelFileIcon}
                          alt=""
                          width={36}
                          height={36}
                        />
                        <div>
                          <p className={styles.fileName}>{item.fileName}</p>
                          <p className={styles.fileMeta}>
                            {formatFileSize(item.fileSizeBytes)}
                            {' · Enviado '}
                            {formatRelativeTime(item.createdAt)}
                          </p>
                        </div>
                      </div>
                      <div className={styles.rowActions}>
                        <span
                          className={`${styles.badge} ${styles[meta.badgeClass]}`}
                          title={item.failureReason || undefined}
                        >
                          <img
                            className={styles.badgeIcon}
                            src={meta.icon}
                            alt=""
                            width={13}
                            height={13}
                          />
                          {meta.label}
                        </span>
                        <button
                          type="button"
                          className={styles.closeButton}
                          aria-label={`Ocultar ${item.fileName}`}
                          onClick={() => setDismissedIds((current) => [...current, item.id])}
                        >
                          <img src={closeIcon} alt="" width={28} height={28} />
                        </button>
                      </div>
                    </div>
                    {meta.showProgress && (
                      <div className={styles.progressRow}>
                        <div className={styles.progressTrack}>
                          <div className={styles.progressFill} style={{ width: `${progress}%` }} />
                        </div>
                        <span className={styles.progressLabel}>{progress}%</span>
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
        </section>
      )}

      {popUpFeedBack.typeError && (
        <Modal onClose={() => setPopUpFeedBack({ ...popUpFeedBack, typeError: false, message: '' })}>
          <div className={styles.modalContainer}>
            <img src={errorIcon} alt="erro" />
            <p>
              Não foi possível importar o arquivo!
              <br />
              Código do erro: KC-001
            </p>
            {popUpFeedBack.message && <p>{popUpFeedBack.message}</p>}
            <button
              type="button"
              className={styles.btnSubmit}
              onClick={() => setPopUpFeedBack({ ...popUpFeedBack, typeError: false, message: '' })}
            >
              Fechar
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default UploadFileAndList;
